import { useCallback, useEffect, useState } from "react";

/**
 * Whether the page is fullscreen, and a way to toggle it.
 *
 * Tracks the `fullscreenchange` EVENT rather than remembering what the button last did,
 * because fullscreen leaves by routes the button never sees: Escape, F11, the browser's
 * own chrome, switching tabs on some platforms. A local boolean would go stale the first
 * time somebody pressed Escape, leaving a button that says "exit" on a windowed page.
 *
 * Reads `document.fullscreenElement` rather than assuming, for the same reason — this
 * hook can mount into an already-fullscreen page.
 */
export function useFullscreen(): {
  isFullscreen: boolean;
  toggle: () => void;
  /** False where the browser has no fullscreen API at all — iOS Safari on iPhone. */
  supported: boolean;
} {
  const supported =
    typeof document !== "undefined" &&
    !!document.documentElement.requestFullscreen;

  const [isFullscreen, setIsFullscreen] = useState(
    () => typeof document !== "undefined" && !!document.fullscreenElement,
  );

  useEffect(() => {
    const sync = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const toggle = useCallback(() => {
    // Both calls reject rather than throw — a denied request (no user gesture, or a
    // policy block) must not take the page down with it, and there is nothing useful to
    // tell the user that the unchanged button doesn't already say.
    if (document.fullscreenElement)
      void document.exitFullscreen().catch(() => undefined);
    else
      void document.documentElement.requestFullscreen().catch(() => undefined);
  }, []);

  return { isFullscreen, toggle, supported };
}
